import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnMeta } from '@/types/dataset';
import type { ColumnTransform } from '@/types/query';
import { configDb } from '@/persistence/config-db';
import { useDatasetStore } from '@/store/dataset-store';
import { getQueryWorker } from '@/engine/worker-client';
import { isNumericSqlType } from '@/engine/widget-spec';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { MapEditor } from './MapEditor';
import { BinEditor } from './BinEditor';

type Props = {
  datasetId: string;
  columns: readonly ColumnMeta[];
  current: Record<string, ColumnTransform>;
  onClose: () => void;
};

/**
 * Modal for per-column value groupings (categorical synonym mapping +
 * numeric bucketing). Draft is staged locally; on "Uygula" the full map
 * is persisted to Dexie, pushed into the dataset store, and the worker's
 * query cache is invalidated so charts repaint with the new labels.
 */
export function ValueGroupingsModal({
  datasetId,
  columns,
  current,
  onClose,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const trapRef = useFocusTrap();
  const setValueGroupings = useDatasetStore((s) => s.setValueGroupings);
  const [draft, setDraft] =
    useState<Record<string, ColumnTransform>>(() => ({ ...current }));
  const [selectedCol, setSelectedCol] = useState<string | null>(
    columns[0]?.name ?? null,
  );
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filteredColumns = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return columns;
    return columns.filter((c) => c.name.toLowerCase().includes(q));
  }, [columns, search]);

  const selectedMeta = columns.find((c) => c.name === selectedCol);
  const isNumericCol = selectedMeta
    ? isNumericSqlType(selectedMeta.sqlType)
    : false;

  const selectedDraft = selectedCol ? draft[selectedCol] : undefined;

  function commitColumnDraft(col: string, next: ColumnTransform | null): void {
    setDraft((prev) => {
      const copy = { ...prev };
      if (next === null) delete copy[col];
      else copy[col] = next;
      return copy;
    });
  }

  function clearColumn(): void {
    if (!selectedCol) return;
    commitColumnDraft(selectedCol, null);
  }

  async function apply(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await configDb.valueGroupings.put({
        datasetId,
        groupings: draft,
        updatedAt: Date.now(),
      });
      setValueGroupings(datasetId, draft);
      // Cached query results reference the old transform set — flush.
      await getQueryWorker().invalidateCache(datasetId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div
      ref={trapRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 outline-none"
      style={{ background: 'rgba(0,0,0,0.45)' }}
    >
      <div
        className="w-full max-w-[900px] max-h-[86vh] flex flex-col gap-3 p-4 rounded-lg shadow-xl"
        style={{
          background: 'var(--color-bg)',
          border: '1px solid var(--color-border)',
        }}
      >
        <div className="flex items-center justify-between">
          <h2
            className="text-sm font-semibold"
            style={{ color: 'var(--color-fg)' }}
          >
            {t('valueGroupings.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="text-lg px-2 rounded hover:opacity-80"
            style={{ color: 'var(--color-muted)' }}
          >
            ×
          </button>
        </div>

        <div className="flex gap-3 flex-1 min-h-0">
          {/* Left: column list */}
          <div className="w-[240px] flex flex-col gap-2 min-h-0">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('valueGroupings.searchColumn')}
              className="text-xs p-1.5 rounded"
              style={{ border: '1px solid var(--color-border)' }}
            />
            <div
              className="flex-1 overflow-auto rounded p-1"
              style={{ border: '1px solid var(--color-border)' }}
            >
              {filteredColumns.length === 0 && (
                <div
                  className="text-xs p-2"
                  style={{ color: 'var(--color-muted)' }}
                >
                  {t('valueGroupings.noColumnMatch')}
                </div>
              )}
              {filteredColumns.map((c) => {
                const has = !!draft[c.name];
                const active = c.name === selectedCol;
                return (
                  <button
                    key={c.name}
                    type="button"
                    onClick={() => setSelectedCol(c.name)}
                    className="w-full flex items-center gap-2 px-2 py-1 text-xs rounded text-left hover:opacity-80"
                    style={{
                      background: active ? 'var(--color-surface)' : undefined,
                      color: 'var(--color-fg)',
                    }}
                  >
                    <span className="flex-1 truncate" title={c.name}>
                      {c.name}
                    </span>
                    {has && (
                      <span
                        className="text-[10px] px-1 rounded"
                        style={{
                          background: 'var(--color-accent)',
                          color: 'var(--color-bg)',
                        }}
                      >
                        •
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: editor */}
          <div
            className="flex-1 flex flex-col gap-2 min-h-0 p-3 rounded"
            style={{ border: '1px solid var(--color-border)' }}
          >
            {!selectedCol && (
              <div
                className="text-xs"
                style={{ color: 'var(--color-muted)' }}
              >
                {t('valueGroupings.selectColumn')}
              </div>
            )}
            {selectedCol && (
              <>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-col min-w-0">
                    <div
                      className="text-xs font-semibold truncate"
                      style={{ color: 'var(--color-fg)' }}
                      title={selectedCol}
                    >
                      {selectedCol}
                    </div>
                    <div
                      className="text-[10px] uppercase"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      {selectedMeta?.sqlType} · {isNumericCol ? t('valueGroupings.modeRange') : t('valueGroupings.modeMap')}
                    </div>
                  </div>
                  {selectedDraft && (
                    <button
                      type="button"
                      onClick={clearColumn}
                      className="text-[11px] px-2 py-0.5 rounded hover:opacity-80"
                      style={{
                        color: 'var(--color-danger)',
                        border: '1px solid var(--color-danger)',
                      }}
                    >
                      {t('valueGroupings.removeGrouping')}
                    </button>
                  )}
                </div>

                <div className="flex-1 min-h-0 overflow-auto">
                  {isNumericCol ? (
                    <BinEditor
                      column={selectedCol}
                      {...(selectedDraft?.kind === 'bin'
                        ? { value: selectedDraft }
                        : {})}
                      onChange={(v) => commitColumnDraft(selectedCol, v)}
                    />
                  ) : (
                    <MapEditor
                      datasetId={datasetId}
                      column={selectedCol}
                      {...(selectedDraft?.kind === 'map'
                        ? { value: selectedDraft }
                        : {})}
                      onChange={(v) => commitColumnDraft(selectedCol, v)}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {error && (
          <div
            className="text-xs p-2 rounded"
            role="alert"
            style={{
              color: 'var(--color-danger)',
              border: '1px solid var(--color-danger)',
            }}
          >
            {error}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-xs px-3 py-1 rounded disabled:opacity-60"
            style={{ color: 'var(--color-muted)' }}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              void apply();
            }}
            disabled={busy}
            className="text-xs px-3 py-1 rounded text-white disabled:opacity-60"
            style={{ background: 'var(--color-accent)' }}
          >
            {busy ? t('valueGroupings.applying') : t('common.apply')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
